import { useCallback, useMemo } from 'react';

import { useRouter } from 'next/compat/router';

import analytics from '@repo/analytics';
import WithAuth from '@repo/editor/src/components/WithAuth/WithAuth';
import {
  THEME_SWITCH,
  USER_NAVIGATION_CLICK,
} from '@repo/editor/src/global/analytics/events';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { useTheme } from '@repo/ui/theming';
import IconBook from '@repo/ui-icons/IconBook';
import IconDarkMode from '@repo/ui-icons/IconDarkMode';
import IconExit from '@repo/ui-icons/IconExit';
import IconSettings from '@repo/ui-icons/IconSettings';
import { Avatar } from '@repo/ui-kit/components/core/Avatars';
import { Switch } from '@repo/ui-kit/components/core/Switch';
import { ProfileMenu } from '@repo/ui-kit/components/modules/ProfileMenu';
import { utilityClasses } from '@repo/ui-kit/css/utils';
import { HELP_PAGE_URL } from '@repo/utils/url';

import { useWorkspacesFeatureEnabled } from 'global/growthbookUtils';
import { useWorkspaceStore } from 'stores/workspaceStore';

import { AvatarWrapper, ProfileMenuWrapper } from './styles';

const HeaderProfileMenu: React.FC = () => {
  const router = useRouter();
  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);
  const logout = useUserStore(userStoreSelector.logout);
  const setIsSigningOut = useUserStore(userStoreSelector.setIsSigningOut);
  const { toggleTheme, isDarkTheme } = useTheme();
  const workspacesEnabled = useWorkspacesFeatureEnabled();
  const handleThemeChange = useCallback((): void => {
    // Theme change is also handled within the ProfileMenu component but for some reason we don't respect it
    const newTheme = toggleTheme();
    analytics.track(THEME_SWITCH, {
      theme: newTheme,
    });
  }, [toggleTheme]);

  const handleHelp = useCallback((): void => {
    analytics.track(USER_NAVIGATION_CLICK, {
      label: 'help',
    });
    window.location.href = HELP_PAGE_URL;
  }, []);

  const handleLogout = useCallback(async () => {
    setIsSigningOut(true);
    await logout();
    useWorkspaceStore.getState().setActiveWorkspaceId(undefined);

    await updateUserInfo(); // refresh the user store after logout
    await router?.push('/', undefined, { shallow: false });
    setIsSigningOut(false);
  }, [router, logout, updateUserInfo, setIsSigningOut]);

  const handleSettings = useCallback(async () => {
    await router?.push('/user/settings/account', undefined, { shallow: false });
  }, [router]);

  const newWorkspaceProfileMenuActions = useMemo(
    () => [
      {
        title: 'Account Settings',
        onClick: handleSettings,
        appendDivider: false,
        icon: <IconSettings color="currentColor" size={16} />,
      },
      {
        component: (
          <>
            <IconDarkMode color="currentColor" size={16} />
            <Switch
              checked={isDarkTheme}
              color="inherit"
              dataTestId="dark-mode-toggle"
              id="dark-mode-toggle"
              justifyContent="space-between"
              label="Dark Mode"
            />
          </>
        ),
        onClick: handleThemeChange,
        appendDivider: true,
      },
      {
        title: 'Help',
        onClick: handleHelp,
        appendDivider: false,
        icon: <IconBook color="currentColor" size={16} />,
      },
      {
        title: 'Sign Out',
        onClick: handleLogout,
        appendDivider: false,
        icon: <IconExit color="currentColor" size={16} />,
      },
    ],
    [handleHelp, handleLogout, handleSettings, handleThemeChange, isDarkTheme]
  );

  const oldProfileMenuActions = useMemo(
    () => [
      {
        component: (
          <>
            <IconDarkMode color="currentColor" size={16} />
            <Switch
              checked={isDarkTheme}
              color="inherit"
              dataTestId="dark-mode-toggle"
              id="dark-mode-toggle"
              justifyContent="space-between"
              label="Dark Mode"
            />
          </>
        ),
        onClick: handleThemeChange,
        appendDivider: false,
      },
      {
        title: 'Help',
        onClick: handleHelp,
        appendDivider: false,
      },
      {
        title: 'Sign Out',
        onClick: handleLogout,
        appendDivider: false,
      },
    ],
    [handleThemeChange, isDarkTheme, handleLogout, handleHelp]
  );

  const profileMenuActions = workspacesEnabled
    ? newWorkspaceProfileMenuActions
    : oldProfileMenuActions;

  return (
    <ProfileMenuWrapper>
      <WithAuth
        renderAuth={(user) => (
          <ProfileMenu.Root>
            <ProfileMenu.Trigger>
              <AvatarWrapper>
                <Avatar
                  fallback={user?.name}
                  fluid={true}
                  src={user?.profileImage || ''}
                />
              </AvatarWrapper>
            </ProfileMenu.Trigger>
            <ProfileMenu.Content>
              <ProfileMenu.Profile
                email={workspacesEnabled ? user?.email || '' : ''}
                name={user?.name || ''}
                profileImage={user?.profileImage || ''}
              />
              {!workspacesEnabled && (
                <span className={utilityClasses.divider} />
              )}

              <ProfileMenu.Actions actions={profileMenuActions} />
            </ProfileMenu.Content>
          </ProfileMenu.Root>
        )}
      />
    </ProfileMenuWrapper>
  );
};

export default withEventScope(HeaderProfileMenu, {
  module: 'headerProfileMenu',
});
